import type { AnimatedDemo as AnimatedDemoData } from '@portfolio/shared';
import { useCallback, useEffect, useId, useRef, useState, type JSX } from 'react';

import { cn } from '@/lib/cn';
import { usePrefersReducedMotion } from '@/lib/usePrefersReducedMotion';

interface AnimatedDemoProps {
  demo: AnimatedDemoData;
  className?: string;
}

function PlayIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}

function PauseIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path d="M8 5h3v14H8zm5 0h3v14h-3z" fill="currentColor" />
    </svg>
  );
}

/**
 * Plays an animated recording automatically, from its first frame.
 *
 * Three details make autoplay of a multi-megabyte looping GIF workable:
 *
 * - **It starts when it scrolls into view, not on page load.** The animation is
 *   heavier than the whole JavaScript bundle, so downloading it up front would
 *   compete with the content above it. Loading on approach also means the
 *   visitor sees it from frame one rather than arriving mid-loop.
 * - **A pause control is always present.** WCAG 2.2.2 requires a way to stop
 *   motion that runs for more than five seconds and starts on its own.
 * - **`prefers-reduced-motion` is honoured.** Visitors who have asked their OS
 *   to reduce motion get the still frame and an explicit play button instead.
 *
 * Replaying re-assigns the source with a changed fragment, which restarts the
 * animation from the beginning without another network request.
 */
export function AnimatedDemo({ demo, className }: AnimatedDemoProps): JSX.Element {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const mediaId = useId();

  const startPlaying = useCallback(() => {
    setPlayCount((count) => count + 1);
    setIsPlaying(true);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || isPlaying || playCount > 0) {
      return;
    }

    const element = containerRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          startPlaying();
        }
      },
      // Start fetching slightly before it is on screen so the first frame is
      // ready by the time it is.
      { rootMargin: '250px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [prefersReducedMotion, isPlaying, playCount, startPlaying]);

  const toggle = (): void => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    startPlaying();
  };

  // The fragment changes per play so the browser restarts the animation from
  // frame one. Fragments are not sent to the server, so this is served from
  // cache rather than downloaded again.
  const animatedSrc = playCount > 0 ? `${demo.src}#play=${playCount}` : demo.src;

  return (
    <figure ref={containerRef} className={cn('flex flex-col gap-4', className)}>
      <div className="relative overflow-hidden rounded-lg border border-line bg-[#0b0f17] shadow-card">
        <img
          id={mediaId}
          src={isPlaying ? animatedSrc : demo.poster}
          width={demo.width}
          height={demo.height}
          alt={demo.alt}
          decoding="async"
          className="block h-auto w-full"
        />

        {/*
          The label is explicit rather than composed from visible text plus a
          hidden suffix, which produced inconsistent internal spacing. It still
          begins with the visible word, as WCAG 2.5.3 (Label in Name) requires.
        */}
        <button
          type="button"
          aria-pressed={isPlaying}
          aria-controls={mediaId}
          aria-label={`${isPlaying ? 'Pause' : 'Play'} the terminal recording`}
          onClick={toggle}
          className={cn(
            'absolute bottom-3 right-3 inline-flex min-h-11 items-center gap-2 rounded-md',
            'bg-accent-solid px-4 py-2.5 text-sm font-medium text-accent-contrast',
            'transition-colors duration-150 hover:bg-accent-solid-hover',
          )}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
          {isPlaying ? 'Pause' : 'Play'}
        </button>
      </div>

      <figcaption className="text-sm text-muted">
        {demo.caption}
        {demo.credit ? (
          <>
            {' '}
            <span className="text-subtle">
              Recording from{' '}
              {demo.credit.href ? (
                <a
                  href={demo.credit.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-accent underline underline-offset-2 hover:text-accent-strong"
                >
                  {demo.credit.label}
                </a>
              ) : (
                demo.credit.label
              )}
              .
            </span>
          </>
        ) : null}
      </figcaption>
    </figure>
  );
}
